import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLiveUser } from '@/hooks/useLiveUpdates';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, Briefcase, MapPin, Clock, Plus, Lightning } from '@phosphor-icons/react';

const STATUS_COLORS = {
  open: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  accepted: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  in_progress: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  disputed: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
};

export default function MyJobsPage() {
  const { user, token, api } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatedJobIds, setUpdatedJobIds] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/jobs/my/customer');
        setJobs(data.jobs || []);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [api]);

  // Live bid count updates
  const handleBidReceived = useCallback((event) => {
    setJobs(prev => prev.map(j =>
      j.id === event.job_id ? { ...j, bid_count: event.bid_count || (j.bid_count || 0) + 1 } : j
    ));
    setUpdatedJobIds(prev => new Set([...prev, event.job_id]));
    setTimeout(() => setUpdatedJobIds(prev => { const next = new Set(prev); next.delete(event.job_id); return next; }), 4000);
  }, []);

  useLiveUser(token, { onBidReceived: handleBidReceived });

  const filtered = filter === 'all' ? jobs : jobs.filter(j => j.status === filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="my-jobs-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
          <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">My Jobs</h1>
          <Link to="/post-job" className="bg-[#E86A33] text-white font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1" data-testid="post-new-job"><Plus size={14} /> New Job</Link>
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {['all', 'open', 'accepted', 'in_progress', 'completed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filter === f ? 'bg-[#1A4D2E]/10 border-[#1A4D2E] text-[#1A4D2E] border' : 'bg-[#E8DDD2] text-[#5C5C5C] border border-transparent'}`} data-testid={`my-jobs-filter-${f}`}>
              {f === 'all' ? 'All' : f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </header>

      <div className="p-4">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-3 text-center">
            <div className="font-heading text-xl font-bold text-[#1C1C1C]">{jobs.length}</div>
            <div className="text-[10px] text-[#5C5C5C] font-bold">Total</div>
          </div>
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-3 text-center">
            <div className="font-heading text-xl font-bold text-blue-600">{jobs.filter(j => j.status === 'open').length}</div>
            <div className="text-[10px] text-[#5C5C5C] font-bold">Open</div>
          </div>
          <div className="bg-white border border-[#D4C5B3] rounded-xl p-3 text-center">
            <div className="font-heading text-xl font-bold text-emerald-600">{jobs.filter(j => j.status === 'completed').length}</div>
            <div className="text-[10px] text-[#5C5C5C] font-bold">Done</div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-jobs">
            <Briefcase size={48} className="text-[#E5E3DB] mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-2">No jobs yet</h3>
            <p className="text-sm text-[#5C5C5C] mb-4 font-body">Post your first job and start receiving bids.</p>
            <Link to="/post-job" className="inline-block bg-[#E86A33] text-white font-bold px-6 py-3 rounded-xl font-body" data-testid="post-first-job">Post a Job</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(job => {
              const sc = STATUS_COLORS[job.status] || STATUS_COLORS.open;
              return (
                <Link key={job.id} to={`/job/${job.id}`} className="block bg-white border border-[#D4C5B3] rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`my-job-${job.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 bg-[#1A4D2E]/10 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                      {job.category === 'plumbing' ? '🔧' : job.category === 'electrical' ? '⚡' : '🔨'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C]">{job.category?.replace(/_/g, ' ')}</p>
                      <h3 className="text-sm font-bold text-[#1C1C1C] truncate font-body">{job.title}</h3>
                      <p className="text-xs text-[#5C5C5C] flex items-center gap-1 mt-1"><MapPin size={12} />{job.lga}, {job.state}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-[#D4C5B3]">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text} border ${sc.border}`}>{job.status?.replace(/_/g, ' ')}</span>
                    <span className={`text-xs font-body flex items-center gap-1 ${updatedJobIds.has(job.id) ? 'text-[#E86A33] font-bold' : 'text-[#5C5C5C]'}`}>
                      {updatedJobIds.has(job.id) && <Lightning size={12} weight="fill" className="text-[#E86A33] animate-pulse" />}
                      <span className={updatedJobIds.has(job.id) ? 'animate-[bidPulse_0.6s_ease-in-out]' : ''}>{job.bid_count || 0}</span> bid{(job.bid_count || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
